import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import SEO from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import SignInModal from '../../components/SignInModal';
import ImageCropper from '../../components/ImageCropper';
import {
  Lock,
  User,
  Building,
  Palette,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Check,
  Camera,
  Monitor,
  Briefcase,
  Image as ImageIcon,
  Sparkles,
  Crown,
  Upload,
  X,
  Download,
  QrCode,
  Mail,
  Eye,
  Crop,
  Loader2
} from 'lucide-react';

const STEPS = [
  { id: 1, title: 'Your Info', icon: User },
  { id: 2, title: 'Photo', icon: Camera },
  { id: 3, title: 'Background', icon: Palette },
];

const CATEGORIES = [
  { id: 'all', name: 'All' },
  { id: 'professional', name: 'Professional' },
  { id: 'creative', name: 'Creative' },
  { id: 'minimal', name: 'Minimal' },
  { id: 'nature', name: 'Nature' },
  { id: 'custom', name: 'My Uploads' },
];

// Storage key for persisting selections
const STORAGE_KEY = 'meeting_theme_generator_data';

const MeetingThemesGenerator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  const bgUploadRef = useRef(null);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    company: '',
    email: '',
  });
  
  const [profileImage, setProfileImage] = useState(null);
  const [backgrounds, setBackgrounds] = useState([]);
  const [customBackgrounds, setCustomBackgrounds] = useState([]);
  const [selectedBackground, setSelectedBackground] = useState(null);
  const [bgCategory, setBgCategory] = useState('all');
  const [loadingBackgrounds, setLoadingBackgrounds] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [pendingSignIn, setPendingSignIn] = useState(false);
  
  // Image cropping state
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  
  // Download state
  const [isDownloading, setIsDownloading] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  // Swipe gesture state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);
  const minSwipeDistance = 50;

  // Swipe gesture handlers
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchEndX(null);
    setTouchStart(e.targetTouches[0].clientY);
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd || !touchStartX || !touchEndX) return;
    
    const distanceY = touchStart - touchEnd;
    const distanceX = touchStartX - touchEndX;
    
    const isHorizontalSwipe = Math.abs(distanceX) > Math.abs(distanceY);
    
    if (isHorizontalSwipe) {
      const isSwipeLeft = distanceX > minSwipeDistance;
      const isSwipeRight = distanceX < -minSwipeDistance;
      
      if (isSwipeLeft && !showPreview && currentStep < 3) {
        nextStep();
      } else if (isSwipeRight && !showPreview && currentStep > 1) {
        prevStep();
      }
    }
    // Removed swipe up/down for preview - only keep horizontal swipe for step navigation
  };

  // Load saved data from localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        if (parsed.formData) setFormData(parsed.formData);
        if (parsed.profileImage) setProfileImage(parsed.profileImage);
        if (parsed.customBackgrounds) setCustomBackgrounds(parsed.customBackgrounds);
        if (parsed.selectedBackgroundId) {
          // Will be restored after backgrounds load
        }
      } catch (e) {
        console.error('Error loading saved data:', e);
      }
    }
    loadBackgrounds();
  }, []);

  // Save data to localStorage when it changes
  useEffect(() => {
    const dataToSave = {
      formData,
      profileImage,
      customBackgrounds,
      selectedBackgroundId: selectedBackground?.id || null,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
  }, [formData, profileImage, customBackgrounds, selectedBackground]);

  // Restore selected background after backgrounds load
  useEffect(() => {
    if (backgrounds.length > 0 || customBackgrounds.length > 0) {
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          if (parsed.selectedBackgroundId) {
            // Check in regular backgrounds
            const found = backgrounds.find(bg => bg.id === parsed.selectedBackgroundId);
            if (found) {
              setSelectedBackground(found);
              return;
            }
            // Check in custom backgrounds
            const customFound = customBackgrounds.find(bg => bg.id === parsed.selectedBackgroundId);
            if (customFound) {
              setSelectedBackground(customFound);
              return;
            }
          }
        } catch (e) {
          console.error('Error restoring selection:', e);
        }
      }
      // Default to first background if nothing saved
      if (!selectedBackground && backgrounds.length > 0) {
        setSelectedBackground(backgrounds[0]);
      }
    }
  }, [backgrounds, customBackgrounds]);

  const loadBackgrounds = async () => {
    setLoadingBackgrounds(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/backgrounds`);
      if (response.ok) {
        const data = await response.json();
        setBackgrounds(data);
      }
    } catch (error) {
      console.error('Error loading backgrounds:', error);
    } finally {
      setLoadingBackgrounds(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        // Show cropper with the uploaded image
        setImageToCrop(e.target.result);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = (croppedImage) => {
    setProfileImage(croppedImage);
    setImageToCrop(null);
  };

  const handleBackgroundUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast({ title: 'File too large', description: 'Please upload an image under 5MB', variant: 'destructive' });
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        const newBg = {
          id: `custom_${Date.now()}`,
          name: file.name.replace(/\.[^/.]+$/, ''),
          type: 'custom',
          image_url: event.target.result,
          category: 'custom',
          is_custom: true,
        };
        setCustomBackgrounds(prev => [...prev, newBg]);
        setSelectedBackground(newBg);
        setBgCategory('custom');
        toast({ title: 'Background uploaded', description: 'Your custom background has been added' });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeCustomBackground = (bgId) => {
    setCustomBackgrounds(prev => prev.filter(bg => bg.id !== bgId));
    if (selectedBackground?.id === bgId) {
      setSelectedBackground(backgrounds[0] || null);
      setBgCategory('all');
    }
  };

  // Helper function to load images for canvas (handles CORS)
  const loadImage = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = src;
    });
  };

  // Helper function to load images with proxy fallback for CORS issues
  const loadImageForCanvas = async (src) => {
    if (!src) return null;
    
    try {
      // First try direct load
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      return await new Promise((resolve, reject) => {
        img.onload = () => resolve(img);
        img.onerror = async () => {
          // If direct load fails, try via proxy
          try {
            const proxyUrl = `${process.env.REACT_APP_BACKEND_URL}/api/backgrounds/proxy-image?url=${encodeURIComponent(src)}`;
            const proxyImg = new Image();
            proxyImg.crossOrigin = 'anonymous';
            proxyImg.onload = () => resolve(proxyImg);
            proxyImg.onerror = () => reject(new Error('Proxy load failed'));
            proxyImg.src = proxyUrl;
          } catch (e) {
            reject(e);
          }
        };
        img.src = src;
      });
    } catch (e) {
      console.error('Image load error:', e);
      return null;
    }
  };

  // Generate and download the meeting background
  const downloadMeetingBackground = async () => {
    if (!selectedBackground) {
      toast({ title: 'Select Background', description: 'Please select a background first', variant: 'destructive' });
      return;
    }
    
    setIsDownloading(true);
    
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Virtual background size (16:9 aspect ratio for video calls)
      canvas.width = 1920;
      canvas.height = 1080;
      
      // Draw background
      if (selectedBackground.type === 'gradient') {
        const gradient = selectedBackground.gradient;
        const colorMatch = gradient.match(/#[a-fA-F0-9]{6}/g);
        if (colorMatch && colorMatch.length >= 2) {
          const grd = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
          grd.addColorStop(0, colorMatch[0]);
          grd.addColorStop(1, colorMatch[1]);
          ctx.fillStyle = grd;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
      } else if (selectedBackground.type === 'url' && selectedBackground.image_url) {
        try {
          const bgImg = await loadImage(selectedBackground.image_url);
          ctx.drawImage(bgImg, 0, 0, canvas.width, canvas.height);
        } catch (e) {
          console.log('Failed to load URL image, using fallback gradient');
          const grd = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
          grd.addColorStop(0, '#667eea');
          grd.addColorStop(1, '#764ba2');
          ctx.fillStyle = grd;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
      } else if (selectedBackground.type === 'custom' && selectedBackground.image_url) {
        // Custom uploaded background (base64 data URL)
        try {
          const bgImg = await loadImage(selectedBackground.image_url);
          ctx.drawImage(bgImg, 0, 0, canvas.width, canvas.height);
        } catch (e) {
          console.log('Failed to load custom image');
          const grd = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
          grd.addColorStop(0, '#667eea');
          grd.addColorStop(1, '#764ba2');
          ctx.fillStyle = grd;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
      } else if (selectedBackground.thumbnail_url) {
        const imgUrl = selectedBackground.thumbnail_url.startsWith('http') 
          ? selectedBackground.thumbnail_url 
          : `${process.env.REACT_APP_BACKEND_URL}${selectedBackground.thumbnail_url}`;
        const bgImg = await loadImage(imgUrl);
        ctx.drawImage(bgImg, 0, 0, canvas.width, canvas.height);
      }
      
      // Add subtle overlay for text readability
      ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // === TOP LEFT: Card Info Panel ===
      const panelWidth = 640;
      const panelHeight = 200;
      const panelX = 50;
      const panelY = 50;
      
      // Draw glass panel background with shadow
      ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
      ctx.shadowBlur = 30;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 8;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.beginPath();
      ctx.roundRect(panelX, panelY, panelWidth, panelHeight, 20);
      ctx.fill();
      ctx.shadowColor = 'transparent';
      
      // Profile image
      let textStartX = panelX + 30;
      let imageLoaded = false;
      
      if (profileImage) {
        try {
          const img = await loadImageForCanvas(profileImage);
          if (img) {
            const imgSize = 140;
            const imgX = panelX + 30;
            const imgY = panelY + (panelHeight - imgSize) / 2;
            
            // Circular clip for profile image
            ctx.save();
            ctx.beginPath();
            ctx.arc(imgX + imgSize/2, imgY + imgSize/2, imgSize/2, 0, Math.PI * 2);
            ctx.clip();
            ctx.drawImage(img, imgX, imgY, imgSize, imgSize);
            ctx.restore();
            
            // Add border to profile image
            ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.arc(imgX + imgSize/2, imgY + imgSize/2, imgSize/2, 0, Math.PI * 2);
            ctx.stroke();
            
            textStartX = imgX + imgSize + 25;
            imageLoaded = true;
          }
        } catch (e) {
          console.log('Could not load profile image:', e);
        }
      }
      
      // Draw placeholder if no image
      if (!imageLoaded) {
        const imgSize = 140;
        const imgX = panelX + 30;
        const imgY = panelY + (panelHeight - imgSize) / 2;
        
        ctx.fillStyle = '#e5e7eb';
        ctx.beginPath();
        ctx.arc(imgX + imgSize/2, imgY + imgSize/2, imgSize/2, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.fillStyle = '#6b7280';
        ctx.font = 'bold 48px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const initials = (formData.name || 'U').charAt(0).toUpperCase();
        ctx.fillText(initials, imgX + imgSize/2, imgY + imgSize/2);
        ctx.textAlign = 'left';
        ctx.textBaseline = 'alphabetic';
        
        textStartX = imgX + imgSize + 25;
      }
      
      // Draw text - Name, Title, Company
      const name = formData.name || 'Your Name';
      const title = formData.title || '';
      const company = formData.company || '';
      
      ctx.fillStyle = '#1a1a1a';
      ctx.font = 'bold 40px Arial, sans-serif';
      ctx.fillText(name, textStartX, panelY + 70);
      
      if (title) {
        ctx.fillStyle = '#555';
        ctx.font = '28px Arial, sans-serif';
        ctx.fillText(title, textStartX, panelY + 115);
      }
      
      if (company) {
        ctx.fillStyle = '#777';
        ctx.font = 'italic 26px Arial, sans-serif';
        ctx.fillText(company, textStartX, panelY + 155);
      }
      
      // === TOP RIGHT: QR Code Panel ===
      const qrPanelSize = 352;
      const qrPanelX = canvas.width - qrPanelSize - 50;
      const qrPanelY = 35;
      
      ctx.shadowColor = 'rgba(0, 0, 0, 0.25)';
      ctx.shadowBlur = 30;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 10;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.98)';
      ctx.beginPath();
      ctx.roundRect(qrPanelX, qrPanelY, qrPanelSize, qrPanelSize, 24);
      ctx.fill();
      ctx.shadowColor = 'transparent';
      
      // Generate QR code with email or a placeholder URL
      const qrPadding = 25;
      const qrSize = qrPanelSize - (qrPadding * 2);
      const qrX = qrPanelX + qrPadding;
      const qrY = qrPanelY + qrPadding;
      
      const qrContent = formData.email 
        ? `mailto:${formData.email}` 
        : `${window.location.origin}`;
      const qrColor = '7c3aed';
      const qrUrl = `https://quickchart.io/qr?text=${encodeURIComponent(qrContent)}&size=600&dark=${qrColor}&light=ffffff&margin=2`;
      
      try {
        const qrImg = await loadImage(qrUrl);
        ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
      } catch (e) {
        console.log('Could not load QR code');
        ctx.fillStyle = '#e5e7eb';
        ctx.fillRect(qrX, qrY, qrSize, qrSize);
        ctx.fillStyle = '#999';
        ctx.font = '16px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('QR Code', qrX + qrSize/2, qrY + qrSize/2);
        ctx.textAlign = 'left';
      }
      
      // Add watermark
      const watermarkText = 'Created with pipercard.com';
      ctx.font = '18px Arial, sans-serif';
      const textWidth = ctx.measureText(watermarkText).width;
      
      const watermarkX = (canvas.width - textWidth) / 2;
      const watermarkY = canvas.height - 30;
      
      ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
      ctx.beginPath();
      ctx.roundRect(watermarkX - 20, watermarkY - 18, textWidth + 40, 32, 16);
      ctx.fill();
      
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.fillText(watermarkText, watermarkX, watermarkY);
      
      // Convert to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${formData.name || 'meeting'}_background.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast({
          title: "Background Downloaded!",
          description: "Set this as your virtual background in Zoom, Google Meet, or Teams."
        });
        
        setIsDownloading(false);
        setShowPreview(false);
        setShowDownloadModal(true);
      }, 'image/png');
      
    } catch (error) {
      console.error('Error generating background:', error);
      toast({
        title: "Error",
        description: "Failed to generate background. Please try again.",
        variant: "destructive"
      });
      setIsDownloading(false);
    }
  };

  const nextStep = () => {
    if (currentStep === 1 && !formData.name.trim()) {
      toast({ title: 'Name Required', description: 'Please enter your name', variant: 'destructive' });
      return;
    }
    if (currentStep === 1 && !formData.email.trim()) {
      toast({ title: 'Email Required', description: 'Please enter your email address', variant: 'destructive' });
      return;
    }
    if (currentStep < 3) {
      setCurrentStep(prev => prev + 1);
    } else {
      setShowPreview(true);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };

  // Handle download/create card action
  const handleCreateAndDownload = async () => {
    if (!user) {
      setPendingSignIn(true);
      setShowSignInModal(true);
      return;
    }
    // User is signed in - download the background directly
    await downloadMeetingBackground();
  };

  // Handle post-sign-in - show preview with selected background for download
  useEffect(() => {
    if (user && pendingSignIn) {
      setPendingSignIn(false);
      setShowSignInModal(false);
      
      // Restore selected background from localStorage if not set
      if (!selectedBackground) {
        const savedData = localStorage.getItem(STORAGE_KEY);
        if (savedData) {
          try {
            const parsed = JSON.parse(savedData);
            if (parsed.selectedBackgroundId) {
              const found = backgrounds.find(bg => bg.id === parsed.selectedBackgroundId);
              if (found) {
                setSelectedBackground(found);
              } else {
                const customFound = customBackgrounds.find(bg => bg.id === parsed.selectedBackgroundId);
                if (customFound) {
                  setSelectedBackground(customFound);
                }
              }
            }
          } catch (e) {
            console.error('Error restoring background:', e);
          }
        }
      }
      
      // Show preview popup with the selected background - user will click download
      setTimeout(() => {
        setShowPreview(true);
        toast({
          title: "Welcome!",
          description: "Click 'Download Background' to save your creation."
        });
      }, 300);
    }
  }, [user, pendingSignIn, backgrounds, customBackgrounds]);

  // Get all backgrounds for display
  const getAllBackgrounds = () => {
    if (bgCategory === 'custom') {
      return customBackgrounds;
    }
    if (bgCategory === 'all') {
      return [...backgrounds, ...customBackgrounds];
    }
    return backgrounds.filter(bg => bg.category === bgCategory);
  };

  const filteredBackgrounds = getAllBackgrounds();

  // Get background image/gradient for preview
  const getBackgroundStyle = (bg) => {
    if (!bg) return { background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)' };
    if (bg.type === 'gradient') {
      return { background: bg.gradient };
    } else if (bg.type === 'url' || bg.type === 'custom') {
      return { backgroundImage: `url(${bg.image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' };
    } else if (bg.thumbnail_url) {
      const url = bg.thumbnail_url?.startsWith('http') ? bg.thumbnail_url : `${process.env.REACT_APP_BACKEND_URL}${bg.thumbnail_url}`;
      return { backgroundImage: `url(${url})`, backgroundSize: 'cover', backgroundPosition: 'center' };
    }
    return { background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)' };
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-sm mb-1.5 block">Your Name <span className="text-red-500">*</span></Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="John Doe"
                  className="pl-10 h-12"
                  autoFocus
                  data-testid="input-name"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm mb-1.5 block">Job Title</Label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="Senior Product Manager"
                  className="pl-10 h-12"
                  data-testid="input-title"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm mb-1.5 block">Company</Label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={formData.company}
                  onChange={(e) => handleChange('company', e.target.value)}
                  placeholder="Acme Corporation"
                  className="pl-10 h-12"
                  data-testid="input-company"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm mb-1.5 block">Email <span className="text-red-500">*</span></Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="john@acme.com"
                  className="pl-10 h-12"
                  data-testid="input-email"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium block">Profile Photo (Optional)</Label>
            <p className="text-xs text-gray-500 -mt-2">Add your photo to personalize your meeting background</p>
            
            <div className="flex items-center gap-4">
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="w-24 h-24 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-all overflow-hidden"
              >
                {profileImage ? (
                  <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center">
                    <Camera className="w-8 h-8 text-gray-400 mx-auto" />
                    <span className="text-xs text-gray-500 mt-1">Add Photo</span>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-2">Upload a professional photo</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-sm"
                >
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Choose File
                </Button>
                {profileImage && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setProfileImage(null)}
                    className="ml-2 text-red-500 hover:text-red-600"
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium block">
                <Palette className="w-4 h-4 inline mr-1" /> Choose Background
              </Label>
              <Button
                variant="outline"
                size="sm"
                onClick={() => bgUploadRef.current?.click()}
                className="text-xs"
              >
                <Upload className="w-3.5 h-3.5 mr-1" />
                Upload Custom
              </Button>
            </div>
            
            <input
              ref={bgUploadRef}
              type="file"
              accept="image/*"
              onChange={handleBackgroundUpload}
              className="hidden"
            />
            
            {/* Category Filters */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setBgCategory(cat.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                    bgCategory === cat.id
                      ? 'bg-purple-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {cat.name}
                  {cat.id === 'custom' && customBackgrounds.length > 0 && (
                    <span className="ml-1 bg-white/20 px-1.5 py-0.5 rounded-full text-[10px]">
                      {customBackgrounds.length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Background Grid */}
            {loadingBackgrounds ? (
              <div className="text-center py-8">
                <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">Loading backgrounds...</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 max-h-[450px] overflow-y-auto pr-1">
                {filteredBackgrounds.map((bg) => (
                  <button
                    key={bg.id}
                    onClick={() => setSelectedBackground(bg)}
                    className={`relative aspect-video rounded-xl overflow-hidden border-2 transition-all ${
                      selectedBackground?.id === bg.id
                        ? 'border-purple-500 ring-2 ring-purple-500/30'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`bg-${bg.id}`}
                  >
                    {bg.type === 'gradient' ? (
                      <div className="w-full h-full" style={{ background: bg.gradient }} />
                    ) : bg.type === 'url' || bg.type === 'custom' ? (
                      <img src={bg.image_url} alt={bg.name} className="w-full h-full object-cover" />
                    ) : (
                      <img
                        src={bg.thumbnail_url?.startsWith('http') ? bg.thumbnail_url : `${process.env.REACT_APP_BACKEND_URL}${bg.thumbnail_url}`}
                        alt={bg.name}
                        className="w-full h-full object-cover"
                      />
                    )}
                    
                    {/* Overlay info */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end p-2">
                      <span className="text-white text-xs font-medium truncate">{bg.name}</span>
                    </div>
                    
                    {/* Custom badge & remove button */}
                    {bg.is_custom && (
                      <>
                        <div className="absolute top-1 left-1 bg-purple-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                          Custom
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); removeCustomBackground(bg.id); }}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </>
                    )}
                    
                    {/* Premium badge */}
                    {bg.is_premium && (
                      <div className="absolute top-1 right-1 bg-amber-500 text-white text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                        <Crown className="w-3 h-3" />
                        Pro
                      </div>
                    )}
                    
                    {/* Selected checkmark */}
                    {selectedBackground?.id === bg.id && (
                      <div className="absolute top-1 left-1 bg-purple-500 text-white rounded-full p-0.5">
                        <Check className="w-3 h-3" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}

            {filteredBackgrounds.length === 0 && !loadingBackgrounds && (
              <div className="text-center py-8 text-gray-500">
                <Monitor className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">
                  {bgCategory === 'custom' ? 'No custom backgrounds yet. Upload one!' : 'No backgrounds found in this category'}
                </p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Layout>
      <SEO
        title="Free Meeting Background Generator | Zoom, Teams, Google Meet"
        description="Create professional virtual backgrounds for Zoom, Microsoft Teams, Google Meet. Free meeting theme generator with your name and company branding."
        keywords="meeting background generator, Zoom background, Teams background, virtual meeting backdrop, professional video call background"
      />

      {/* Full-width layout for desktop */}
      <div 
        className="min-h-screen bg-gray-50"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Monitor className="w-6 h-6 text-purple-500" />
              <span className="font-bold text-lg text-gray-900">Meeting Background Generator</span>
            </div>
            <button
              onClick={() => setShowPreview(true)}
              className="text-purple-600 font-medium text-sm hover:text-purple-700"
              data-testid="preview-button"
            >
              Preview
            </button>
          </div>
        </header>

        {/* Main Content - Full Width on Desktop */}
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10">
            {/* Left Column: Steps Form */}
            <div>
              {/* Progress Steps */}
              <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between">
                  {STEPS.map((step, index) => (
                    <div key={step.id} className="flex items-center">
                      <button
                        onClick={() => {
                          if (step.id < currentStep || (step.id === 2 && formData.name) || (step.id === 3 && formData.name)) {
                            setCurrentStep(step.id);
                          }
                        }}
                        className={`flex items-center gap-2 ${currentStep >= step.id ? 'text-purple-600' : 'text-gray-400'}`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                          currentStep > step.id ? 'bg-purple-500 text-white' :
                          currentStep === step.id ? 'bg-purple-100 text-purple-600 border-2 border-purple-500' :
                          'bg-gray-100 text-gray-400'
                        }`}>
                          {currentStep > step.id ? <Check className="w-4 h-4" /> : step.id}
                        </div>
                        <span className="text-sm font-medium hidden sm:inline">{step.title}</span>
                      </button>
                      {index < STEPS.length - 1 && (
                        <div className={`w-8 sm:w-16 h-0.5 mx-2 ${currentStep > step.id ? 'bg-purple-500' : 'bg-gray-200'}`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Step Content */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
                <div className="mb-4">
                  <h2 className="text-xl font-bold text-gray-900">
                    {STEPS[currentStep - 1].title}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {currentStep === 1 && "Enter your information for the background"}
                    {currentStep === 2 && "Add an optional profile photo"}
                    {currentStep === 3 && "Select or upload a background"}
                  </p>
                </div>
                {renderStepContent()}
              </div>

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between gap-4">
                {currentStep > 1 ? (
                  <Button variant="outline" onClick={prevStep} className="flex-1 h-12" data-testid="back-button">
                    <ChevronLeft className="w-5 h-5 mr-1" /> Back
                  </Button>
                ) : (
                  <div className="flex-1" />
                )}
                
                <Button
                  onClick={nextStep}
                  className="flex-1 h-12 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                  data-testid="next-button"
                >
                  {currentStep === 3 ? (
                    <>Preview & Download</>
                  ) : (
                    <>Next <ChevronRight className="w-5 h-5 ml-1" /></>
                  )}
                </Button>
              </div>

              {/* Navigation removed - preview is desktop-only */}
            </div>

            {/* Right Column: Live Preview (Desktop Only) */}
            <div className="hidden lg:block">
              <div className="sticky top-24">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Monitor className="w-5 h-5 text-purple-500" />
                  Live Preview
                </h3>
                
                {/* Preview Container - Bio Page Style */}
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  {/* Preview with selected background */}
                  <div 
                    className="relative rounded-xl overflow-hidden mb-4"
                    style={{ 
                      ...getBackgroundStyle(selectedBackground),
                      aspectRatio: '16/9',
                    }}
                  >
                    {/* Overlay for readability */}
                    <div className="absolute inset-0 bg-black/10" />
                    
                    {/* Top-left: Info card preview (Bio page style) */}
                    <div className="absolute top-3 left-3 bg-white/95 rounded-lg p-2.5 flex items-center gap-2.5 shadow-lg max-w-[60%]">
                      {profileImage ? (
                        <img
                          src={profileImage}
                          alt="Profile"
                          className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-purple-500" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{formData.name || 'Your Name'}</p>
                        {formData.title && <p className="text-xs text-gray-600 truncate">{formData.title}</p>}
                        {formData.company && <p className="text-[10px] text-gray-500 truncate">{formData.company}</p>}
                      </div>
                    </div>
                    
                    {/* Top-right: QR code preview */}
                    <div className="absolute top-3 right-3 bg-white/98 rounded-xl p-2.5 shadow-lg">
                      <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                        <QrCode className="w-10 h-10 text-gray-400" />
                      </div>
                    </div>
                  </div>

                  {/* Quick Background Selection */}
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 mb-2 font-medium">Quick Select</p>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {backgrounds.slice(0, 6).map((bg) => (
                        <button
                          key={bg.id}
                          onClick={() => setSelectedBackground(bg)}
                          className={`flex-shrink-0 w-16 h-10 rounded-lg overflow-hidden border-2 transition-all ${
                            selectedBackground?.id === bg.id
                              ? 'border-purple-500 ring-1 ring-purple-500/30'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {bg.type === 'gradient' ? (
                            <div className="w-full h-full" style={{ background: bg.gradient }} />
                          ) : bg.type === 'url' ? (
                            <img src={bg.image_url} alt={bg.name} className="w-full h-full object-cover" />
                          ) : (
                            <img
                              src={bg.thumbnail_url?.startsWith('http') ? bg.thumbnail_url : `${process.env.REACT_APP_BACKEND_URL}${bg.thumbnail_url}`}
                              alt={bg.name}
                              className="w-full h-full object-cover"
                            />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Info Banner */}
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 mb-4">
                    <div className="flex items-start gap-2">
                      <Sparkles className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-purple-800">Download in full resolution</p>
                        <p className="text-xs text-purple-600 mt-0.5">
                          Sign in to download 1920x1080 backgrounds with your info & QR code.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Download Button */}
                  <Button
                    onClick={handleCreateAndDownload}
                    disabled={isDownloading}
                    className="w-full h-12 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                    data-testid="download-button-desktop"
                  >
                    {isDownloading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : user ? (
                      <><Download className="w-5 h-5 mr-2" /> Download Background</>
                    ) : (
                      <><Lock className="w-5 h-5 mr-2" /> Sign In to Download</>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal (Mobile & on "Preview" click) */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto p-0">
          <div className="flex justify-center pt-2 pb-1">
            <div className="w-10 h-1 bg-gray-300 rounded-full" />
          </div>
          
          <DialogHeader className="px-4 pb-3 border-b">
            <DialogTitle className="text-lg font-semibold text-center flex items-center justify-center gap-2">
              <Monitor className="w-5 h-5 text-purple-500" />
              Your Meeting Background
            </DialogTitle>
            {selectedBackground && (
              <p className="text-xs text-center text-gray-500 mt-1">
                Selected: <span className="font-medium text-purple-600">{selectedBackground.name}</span>
              </p>
            )}
          </DialogHeader>
          
          <div className="p-4">
            {/* Category Selection in Preview */}
            <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-4">
              {CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setBgCategory(cat.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                    bgCategory === cat.id
                      ? 'bg-purple-500 text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => bgUploadRef.current?.click()}
                className="ml-2 text-xs"
              >
                <Upload className="w-3.5 h-3.5 mr-1" />
                Upload
              </Button>
            </div>

            {/* Background Grid in Preview */}
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-4 max-h-[160px] overflow-y-auto">
              {filteredBackgrounds.map((bg) => (
                <button
                  key={bg.id}
                  onClick={() => setSelectedBackground(bg)}
                  className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                    selectedBackground?.id === bg.id
                      ? 'border-purple-500 ring-2 ring-purple-500/30'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {bg.type === 'gradient' ? (
                    <div className="w-full h-full" style={{ background: bg.gradient }} />
                  ) : bg.type === 'url' || bg.type === 'custom' ? (
                    <img src={bg.image_url} alt={bg.name} className="w-full h-full object-cover" />
                  ) : (
                    <img
                      src={bg.thumbnail_url?.startsWith('http') ? bg.thumbnail_url : `${process.env.REACT_APP_BACKEND_URL}${bg.thumbnail_url}`}
                      alt={bg.name}
                      className="w-full h-full object-cover"
                    />
                  )}
                  {selectedBackground?.id === bg.id && (
                    <div className="absolute top-1 left-1 bg-purple-500 text-white rounded-full p-0.5">
                      <Check className="w-3 h-3" />
                    </div>
                  )}
                  {bg.is_premium && (
                    <div className="absolute top-1 right-1 bg-amber-500 text-white text-[8px] px-1 py-0.5 rounded-full">
                      Pro
                    </div>
                  )}
                  {bg.is_custom && (
                    <div className="absolute top-1 right-1 bg-purple-500 text-white text-[8px] px-1 py-0.5 rounded-full">
                      Custom
                    </div>
                  )}
                </button>
              ))}
            </div>

            {/* Preview with selected background - Bio Page Style */}
            <div className="border border-gray-200 rounded-xl p-2 bg-gray-50 mb-4">
              <p className="text-[10px] text-gray-500 mb-2 font-medium">Preview (Card info top-left, QR code top-right)</p>
              <div 
                className="relative rounded-lg overflow-hidden"
                style={{ 
                  ...getBackgroundStyle(selectedBackground),
                  aspectRatio: '16/9',
                }}
              >
                {/* Overlay for readability */}
                <div className="absolute inset-0 bg-black/10" />
                
                {/* Top-left: Info card preview */}
                <div className="absolute top-2 sm:top-3 left-2 sm:left-3 bg-white/95 rounded-lg p-1.5 sm:p-2 flex items-center gap-1.5 sm:gap-2 shadow-lg max-w-[55%]">
                  {profileImage ? (
                    <img
                      src={profileImage}
                      alt="Profile"
                      className="w-6 h-6 sm:w-8 sm:h-8 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <User className="w-3 h-3 sm:w-4 sm:h-4 text-purple-500" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-[10px] sm:text-xs font-semibold text-gray-900 truncate">{formData.name || 'Your Name'}</p>
                    {formData.title && <p className="text-[8px] sm:text-[10px] text-gray-600 truncate">{formData.title}</p>}
                  </div>
                </div>
                
                {/* Top-right: QR code preview */}
                <div className="absolute top-1.5 sm:top-2 right-1.5 sm:right-2 bg-white/98 rounded-lg sm:rounded-xl p-1.5 sm:p-2.5 shadow-lg">
                  <div className="w-10 h-10 sm:w-16 sm:h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                    <QrCode className="w-6 h-6 sm:w-10 sm:h-10 text-gray-400" />
                  </div>
                </div>
              </div>
            </div>

            {/* Info Banner */}
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 mb-4">
              <div className="flex items-start gap-2">
                <Sparkles className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-purple-800">Download in full resolution</p>
                  <p className="text-xs text-purple-600 mt-0.5">
                    Sign in to download 1920x1080 backgrounds and upload your own custom images.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Button */}
            <Button
              onClick={handleCreateAndDownload}
              disabled={isDownloading}
              className="w-full h-12 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-lg"
              data-testid="download-button"
            >
              {isDownloading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Generating...
                </>
              ) : user ? (
                <><Download className="w-5 h-5 mr-2" /> Download Background</>
              ) : (
                <><Lock className="w-5 h-5 mr-2" /> Sign In to Download</>
              )}
            </Button>

            {/* Usage tips */}
            <div className="bg-blue-50 rounded-xl p-3 mt-4 text-xs text-blue-800">
              <p className="font-medium mb-1">How to use:</p>
              <ul className="space-y-0.5 text-blue-700">
                <li>• Zoom: Settings → Background & Effects</li>
                <li>• Meet: More options → Visual effects</li>
                <li>• Teams: Settings → Background effects</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sign In Modal */}
      <SignInModal 
        open={showSignInModal} 
        onOpenChange={setShowSignInModal}
        noRedirect={true}
      />

      {/* Image Cropper Modal */}
      <ImageCropper
        open={showCropper}
        onClose={() => {
          setShowCropper(false);
          setImageToCrop(null);
        }}
        imageSrc={imageToCrop}
        onCropComplete={handleCropComplete}
        aspectRatio={1}
        cropShape="rect"
        title="Crop Your Photo"
      />

      {/* Post-Download Success Modal */}
      <Dialog open={showDownloadModal} onOpenChange={setShowDownloadModal}>
        <DialogContent className="w-[90vw] max-w-md p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-center flex items-center justify-center gap-2">
              <Check className="w-6 h-6 text-green-500" />
              Background Downloaded!
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 text-center">
            <p className="text-gray-600">
              Your meeting background has been saved to your downloads folder.
            </p>
            
            <div className="bg-blue-50 rounded-xl p-4 text-left">
              <p className="text-sm font-medium text-blue-800 mb-2">How to use:</p>
              <ul className="space-y-1 text-sm text-blue-700">
                <li>• <strong>Zoom:</strong> Settings → Background & Effects</li>
                <li>• <strong>Google Meet:</strong> More options → Visual effects</li>
                <li>• <strong>Teams:</strong> Settings → Background effects</li>
              </ul>
            </div>
            
            <div className="flex flex-col gap-3 pt-2">
              <Button
                onClick={() => {
                  setShowDownloadModal(false);
                  navigate('/dashboard');
                }}
                className="w-full h-12 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                data-testid="go-to-dashboard-button"
              >
                Go to Dashboard
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowDownloadModal(false);
                  setShowPreview(true);
                }}
                className="w-full"
                data-testid="create-another-button"
              >
                Create Another Background
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default MeetingThemesGenerator;
